import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ChevronRight, ChevronDown, Check } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { LANGS } from '../data/content';

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const links = [
    { to: '/', label: t('nav.home') },
    { to: '/quienes-somos', label: t('nav.about') },
    { to: '/servicios', label: t('nav.services') },
    { to: '/contacto', label: t('nav.contact') },
  ];

  const isActive = (to) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  const solid = scrolled || open || location.pathname !== '/';
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? 'bg-[#0B1F3A]/95 backdrop-blur-md shadow-lg py-3' : 'bg-transparent py-5'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center group">
          <img
            src="/logo-onDark.png"
            alt="Zheng Da Abogados 正大律师事务所"
            className="h-12 md:h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
          />
        </Link>

        {/* Desktop links */}
        <div className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`link-underline text-sm tracking-wide transition-colors ${
                isActive(l.to) ? 'text-[#C6A15B]' : 'text-white/85 hover:text-white'
              } ${lang === 'zh' ? 'font-cn' : ''}`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 text-white/85 hover:text-[#C6A15B] transition-colors text-sm border border-white/20 rounded-full px-3 py-1.5"
              aria-label="Change language"
            >
              <Globe className="w-4 h-4" strokeWidth={1.6} />
              <span className="font-medium">{current.short}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${langOpen ? 'rotate-180' : ''}`} />
            </button>
            <div
              className={`absolute right-0 mt-2 w-40 rounded-md bg-[#0B1F3A] border border-white/10 shadow-2xl overflow-hidden transition-all duration-200 origin-top-right ${
                langOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-95 pointer-events-none'
              }`}
            >
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => { setLang(l.code); setLangOpen(false); }}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                    l.code === lang ? 'text-[#C6A15B] bg-white/5' : 'text-white/80 hover:bg-white/5 hover:text-white'
                  } ${l.code === 'zh' ? 'font-cn' : ''}`}
                >
                  {l.label}
                  {l.code === lang && <Check className="w-4 h-4" />}
                </button>
              ))}
            </div>
          </div>

          <Link
            to="/contacto"
            className={`hidden md:inline-flex gold-btn rounded-full px-5 py-2.5 text-sm font-semibold ${lang === 'zh' ? 'font-cn' : ''}`}
          >
            {t('nav.cta')}
          </Link>

          <button
            className="lg:hidden text-white p-1"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          open ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-5 pb-5 flex flex-col gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`flex items-center justify-between py-3 border-b border-white/10 text-white/90 ${lang === 'zh' ? 'font-cn' : ''}`}
            >
              {l.label}
              <ChevronRight className="w-4 h-4 text-[#C6A15B]" />
            </Link>
          ))}
          <Link
            to="/contacto"
            className={`gold-btn rounded-full px-5 py-3 text-center text-sm font-semibold mt-4 ${lang === 'zh' ? 'font-cn' : ''}`}
          >
            {t('nav.cta')}
          </Link>

          <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-white/10">
            {LANGS.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  l.code === lang ? 'bg-[#C6A15B] text-[#0B1F3A] border-[#C6A15B]' : 'text-white/80 border-white/20'
                } ${l.code === 'zh' ? 'font-cn' : ''}`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}
